import React, { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Form, Button, Badge } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const InventoryStockSummary = () => {
 const [stocks, setStocks] = useState([]);
 const [warehouses, setWarehouses] = useState([]);
 const [filters, setFilters] = usePersistentFilters("inventory_stock_summary_filters", { search: "", warehouse: "" });
 const [loading, setLoading] = useState(false);

 useEffect(() => {
 loadWarehouses();
 }, []);

 useEffect(() => {
 fetchSummary();
 }, [filters.warehouse]);

 const loadWarehouses = async () => {
 try {
 const res = await api.get("/warehouses?limit=500");
 if (res.data.success) setWarehouses(res.data.data || []);
 } catch (_) {
 // ignore
 }
 };

 const fetchSummary = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();
 if (filters.search) queryParams.append("search", filters.search);
 if (filters.warehouse) queryParams.append("warehouse", filters.warehouse);
 const res = await api.get(`/inventory-purchase-orders/stock/summary?${queryParams.toString()}`);
 if (res.data.success) setStocks(res.data.data || []);
 } catch (error) {
 swal("Error", "Failed to load stock summary", "error");
 } finally {
 setLoading(false);
 }
 };

 const exportCsv = () => {
 if (!stocks.length) {
 return swal("Info", "No stock rows to export", "info");
 }

 const headers = ["Warehouse", "Item", "Unit", "On Hand", "Avg Rate", "Last Rate", "Last Received"];
 const rows = stocks.map((row) => [
 row.warehouse?.warehouseName || "-",
 row.itemName || "-",
 row.unit || "-",
 Number(row.quantityOnHand || 0),
 Number(row.averageRate || 0).toFixed(2),
 Number(row.lastRate || 0).toFixed(2),
 row.lastReceivedAt ? new Date(row.lastReceivedAt).toLocaleDateString() : "-",
 ]);

 const csv = [headers, ...rows]
 .map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
 .join("\n");

 const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
 const url = URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = url;
 link.download = "inventory-stock-summary.csv";
 link.click();
 URL.revokeObjectURL(url);
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Stock Summary" motherMenu="Inventory" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Warehouse Stock Summary</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="secondary" as={Link} to="/inventory-dashboard">Back</Button>
 <Button variant="outline-primary" size="sm" onClick={exportCsv}>
 Export CSV
 </Button>
 </div>
 </Card.Header>
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={4}>
 <Form.Control
 placeholder="Search item"
 value={filters.search}
 onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
 onKeyDown={(e) => e.key === "Enter" && fetchSummary()}
 />
 </Col>
 <Col md={4}>
 <Form.Select
 value={filters.warehouse}
 onChange={(e) => setFilters((prev) => ({ ...prev, warehouse: e.target.value }))}
 >
 <option value="">All Warehouses</option>
 {warehouses.map((wh) => (
 <option key={wh._id} value={wh._id}>
 {wh.warehouseName}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Button className="w-100" onClick={fetchSummary}>Search</Button>
 </Col>
 </Row>

 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th className="text-start">Warehouse</th>
 <th className="text-start">Item</th>
 <th className="text-center">Unit</th>
 <th className="text-center">On Hand</th>
 <th className="text-end">Avg Rate</th>
 <th className="text-end">Last Rate</th>
 <th className="text-center">Last Received</th>
 </tr>
 </thead>
 <tbody>
 {!loading && stocks.map((row) => (
 <tr key={row._id}>
 <td className="text-start">{row.warehouse?.warehouseName || "-"}</td>
 <td className="text-start">{row.itemName}</td>
 <td className="text-center">{row.unit}</td>
 <td className="text-center">
 <Badge
 bg={row.quantityOnHand > 0 ? "success" : "secondary"}
 className="px-3 py-2"
 style={{ minWidth: 56 }}
 >
 {Number(row.quantityOnHand || 0)}
 </Badge>
 </td>
 <td className="text-end">Rs. {Number(row.averageRate || 0).toFixed(2)}</td>
 <td className="text-end">Rs. {Number(row.lastRate || 0).toFixed(2)}</td>
 <td className="text-center text-nowrap">
 {row.lastReceivedAt ? new Date(row.lastReceivedAt).toLocaleDateString() : "-"}
 </td>
 </tr>
 ))}
 {!loading && stocks.length === 0 && (
 <tr>
 <td colSpan="7" className="text-center text-muted py-4">No stock data available</td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default InventoryStockSummary;
